/**
 * Studio: Automations (G21, Settings), one brand at a time: rules on the website that show a
 * prompt next to the chat button (they replace W19's proactive prompts), and rules in chats that
 * reply, tag, prioritise, route or make a ticket. Edited as drafts, published as versions.
 */
export const automationsEn = {
  nav: 'Automations',
  title: 'Automations',
  description:
    'Rules that act on their own: prompts on your website, and replies, tags and tickets in chats. A rule runs only once it is published and on.',
  loading: 'Loading the automations',
  brand: 'Brand',
  open: 'Automations',
  openLabel: 'Automations for {{name}}',
  guide: 'How automations work',
  noPermission: 'Your role can’t change automations. Ask a workspace owner or administrator.',
  forbidden: {
    title: 'You can’t see automations',
    description: 'Ask a workspace owner or administrator for a role that may view automations.',
  },
  noBrands: {
    title: 'No brand yet',
    description: 'Add a brand and its website first. Then set up its automations here.',
    action: 'Go to Brands and install',
  },
  new: 'New automation',
  sections: {
    visitor: {
      title: 'On your website',
      description:
        'Prompts next to the chat button that invite visitors to ask. They run in visitors’ browsers.',
    },
    conversation: {
      title: 'In chats',
      description: 'Replies, notes, tags, priorities, teams and tickets while a chat goes on.',
    },
    empty: 'None yet.',
  },
  status: {
    off: 'Off',
    draft: 'Draft only',
    changes: 'Published v{{version}} · unpublished changes',
    published: 'Published v{{version}}',
  },
  list: {
    edit: 'Edit',
    editLabel: 'Edit {{name}}',
    duplicate: 'Duplicate',
    duplicateLabel: 'Duplicate {{name}}',
    delete: 'Delete',
    deleteLabel: 'Delete {{name}}',
    switchLabel: 'Run {{name}}',
    inBrowser: 'Runs in visitors’ browsers',
    neverRan: 'Hasn’t run yet',
    ranOnce: 'Ran once this week',
    ranTimes: 'Ran {{n}} times this week',
    ranLast: '{{ran}} · last run {{when}}',
    copyName: '{{name}} (copy)',
    duplicated: '{{name}} created as a draft.',
    turnedOn: '{{name}} is on.',
    turnedOff: '{{name}} is off.',
    deleted: '{{name}} deleted.',
    deleteTitle: 'Delete {{name}}?',
    deleteBody:
      'Its versions and the record of what it did are deleted too. This can’t be undone. To stop it for now, turn it off instead.',
    deleteConfirm: 'Delete automation',
  },
  gallery: {
    title: 'Start from a template',
    description:
      'Choose a rule to start from. It is saved as a draft, so you can check it before it runs.',
    ready: 'Ready-made rules',
    scratch: 'Start from scratch',
    scratchDescription: 'An empty rule to write yourself.',
    website: 'On your website',
    chat: 'In chats',
    use: 'Use',
    useLabel: 'Use the template {{name}}',
    created: '{{name}} created as a draft.',
    chooseTag: 'You choose the tag.',
  },
  templates: {
    welcome: {
      name: 'Welcome reply',
      description: 'When a chat starts and is waiting for a person, say someone will reply soon.',
    },
    rescue: {
      name: 'Chat rescue',
      description:
        'After 90 seconds without a reply, apologise, ask for an email address and raise the priority.',
    },
    returning: {
      name: 'Returning visitor prompt',
      description: 'On a third visit or later, offer help after 5 seconds.',
    },
    pricing: {
      name: 'Help on the pricing page',
      description: 'After 30 seconds on the pricing page, offer to answer questions.',
    },
    outOfHours: {
      name: 'Out-of-hours reply',
      description: 'Outside business hours, say when you will reply and make the chat a ticket.',
    },
    tagByPage: {
      name: 'Tag by page',
      description: 'Tag chats that start on the checkout page.',
    },
    scratchWebsite: {
      name: 'New website prompt',
      description: 'A prompt on your website.',
    },
    scratchChat: {
      name: 'New chat automation',
      description: 'A rule that acts in chats.',
    },
  },
  limits: {
    title: 'Prompt limits',
    description:
      'However many website automations you have, one visitor sees at most this many prompts.',
    maxPerDay: 'Prompts one visitor sees in a day',
    maxPerDayHint: 'From 1 to 3.',
    dismissHours: 'After a visitor closes one, wait (hours)',
    dismissHoursHint: 'From 1 to 168 (a week).',
    invalid: 'Enter a whole number from {{min}} to {{max}}.',
    save: 'Save limits',
    saved: 'Prompt limits saved for {{name}}.',
  },
  editor: {
    loading: 'Loading the automation',
    back: 'All automations',
    kinds: { visitor: 'Website automation', conversation: 'Chat automation' },
    name: 'Name',
    nameHint: 'For your team. Customers never see it.',
    whenStep: {
      title: 'When',
      description: 'What starts the rule.',
      event: 'Event',
      seconds: {
        page_view: 'Seconds on the page',
        customer_waiting: 'Seconds without a reply',
      },
      secondsHint: 'From {{min}} to {{max}}.',
      kindNote:
        'A website automation stays on the website, and a chat automation in chats. To make the other kind, start a new automation.',
    },
    ifStep: {
      title: 'If',
      description: 'What must be true. Leave it empty to run every time.',
      match: 'Run when',
      all: 'All are true',
      any: 'Any is true',
      condition: 'Condition {{number}}',
      field: 'Fact',
      op: 'Check',
      value: 'Value',
      yes: 'Yes',
      no: 'No',
      empty: 'No conditions: the rule runs every time the event happens.',
      count: '{{n}} of {{max}}',
      add: 'Add a condition',
      added: 'Condition {{number}} added.',
      remove: 'Remove condition {{number}}',
      removeShort: 'Remove',
      removed: 'Condition {{number}} removed.',
      full: 'A rule has at most {{max}} conditions.',
      addValue: 'Add',
      addValueLabel: 'Add to condition {{number}}',
      removeValue: 'Remove {{value}}',
      countryHint: 'Two-letter country codes, such as PK or AE. Press Enter after each.',
      languageHint: 'Language codes, such as ar or en. Press Enter after each.',
      tagHint: 'The brand’s tags could not be loaded. Type a tag and press Enter.',
      noTags: 'This brand has no tags yet. Add them in Saved replies and tags.',
      noTeams: 'This brand has no support teams yet.',
    },
    thenStep: {
      title: 'Then',
      description: 'What the rule does, in this order.',
      action: 'Action {{number}}',
      type: 'Do this',
      count: '{{n}} of {{max}}',
      add: 'Add an action',
      added: 'Action {{number}} added.',
      remove: 'Remove action {{number}}',
      removeShort: 'Remove',
      removed: 'Action {{number}} removed.',
      full: 'A rule has at most {{max}} actions.',
      prompt: 'Prompt',
      message: 'Message',
      messageHint:
        'English is required. A language left empty shows the English. Up to {{max}} characters.',
      note: 'Note for the team',
      tag: 'Tag',
      tagPlaceholder: 'Choose a tag',
      tagUnknown: '“{{tag}}” is not one of this brand’s tags. Choose one before publishing.',
      tagFree: 'Tag',
      tagFreeHint: 'The brand’s tags could not be loaded. Type the tag exactly.',
      team: 'Team',
      teamPlaceholder: 'Choose a team',
      priority: 'Priority',
    },
    fields: {
      page_url: 'Page address',
      referrer: 'Came from (referrer)',
      visit_count: 'Visits so far',
      country: 'Country',
      device: 'Device',
      language: 'Language',
      signed_in: 'Customer is signed in',
      hours_open: 'Within business hours',
      anyone_online: 'Someone on the team is online',
      owner: 'Who has the chat',
      tag: 'Tag',
      team: 'Team',
    },
    ops: {
      contains: 'contains',
      starts_with: 'starts with',
      is: 'is',
      not_contains: 'does not contain',
      at_least: 'is at least',
      at_most: 'is at most',
      is_not: 'is not',
    },
    events: {
      page_view: 'A visitor is on a page',
      chat_started: 'A chat starts',
      customer_waiting: 'A customer is waiting for a reply',
      chat_closed: 'A chat closes',
      offline_message: 'A message is left while nobody can answer',
    },
    actions: {
      show_prompt: 'Show a prompt',
      send_message: 'Send a message',
      add_note: 'Add a private note',
      add_tag: 'Add a tag',
      set_priority: 'Set the priority',
      assign_team: 'Send to a team',
      make_ticket: 'Make a ticket',
    },
    warnings: {
      title: 'This rule can be saved, but not published as it is:',
      field_not_allowed: 'This fact is not known for this kind of automation. Choose another.',
      action_not_allowed: 'This action is not available for this kind of automation.',
      action_repeated: 'This action is already in the rule. A rule does it only once.',
      prompt_required: 'A website automation shows a prompt. Add the action “Show a prompt”.',
      message_needs_people:
        'The AI is answering this chat. A message from this rule would interrupt it — add ‘Who has the chat is not the AI’.',
      message_after_close: 'A message can’t be sent after the chat closed.',
    },
    problems: {
      name: 'Give the automation a name.',
      seconds: 'Enter a whole number of seconds from {{min}} to {{max}}.',
      conditionValue: 'Condition {{number}}: choose or write a value.',
      conditionNumber: 'Condition {{number}}: enter a whole number from {{min}} to {{max}}.',
      conditionLong: 'Condition {{number}}: use at most {{max}} characters.',
      messageEnglish: 'Action {{number}}: write the words in English.',
      messageLong: 'Action {{number}}: use at most {{max}} characters in each language.',
      note: 'Action {{number}}: write the note.',
      tag: 'Action {{number}}: choose a tag.',
      team: 'Action {{number}}: choose a team.',
      noActions: 'Add at least one action.',
    },
    save: 'Save draft',
    publish: 'Publish',
    saved: 'Draft of {{name}} saved.',
    published: '{{name}} published as version {{version}}.',
    unsaved: 'You have changes that are not saved.',
    reset: 'Undo changes',
    enabled: 'On',
    enabledHint: 'Off keeps the rule but never runs it.',
    turnedOn: '{{name}} is on.',
    turnedOff: '{{name}} is off.',
    statusLine: {
      published: 'Version {{version}}, published by {{who}}, {{when}}.',
      publishedBySystem:
        'Version {{version}}, published {{when}} when your prompts were moved here.',
      draft: 'Not published yet, so it doesn’t run.',
      changes: 'The draft has changes that are not published yet.',
      off: 'Turned off, so it doesn’t run.',
    },
    conflict:
      'Someone changed this automation after you opened it. Reload to see their version; your changes here will be lost.',
    reload: 'Reload',
    reloaded: 'Loaded the latest version.',
    versions: {
      title: 'Versions',
      description:
        'Each publication is kept. Restoring copies a version into the draft; publish it to run it again.',
      empty: 'Not published yet.',
      version: 'Version {{version}}',
      by: 'Published by {{who}}, {{when}}',
      bySystem: 'Published {{when}} by Hamnivo',
      current: 'Running now',
      restore: 'Restore',
      restoreLabel: 'Restore version {{version}}',
      restoreTitle: 'Restore version {{version}}?',
      restoreBody:
        'The draft is replaced by version {{version}}. What runs changes only when you publish.',
      restoreConfirm: 'Restore to draft',
      restored: 'Version {{version}} copied into the draft.',
      saveFirst: 'Save or undo your changes first.',
    },
    runs: {
      title: 'Recent runs',
      description: 'What this rule did in chats, newest first.',
      empty: 'It hasn’t run yet.',
      when: 'When',
      chat: 'Chat',
      actions: 'Actions',
      result: 'Result',
      open: 'Open the chat',
      noChat: 'No chat',
      version: 'v{{version}}',
      outcomes: { done: 'Done', skipped: 'Skipped', failed: 'Failed' },
      reasons: {
        ai_has_chat: 'Message left out: the AI had the chat.',
        message_limit: 'Message left out: the daily limit of automated messages was reached.',
        no_bot: 'Message left out: the chat has no bot to send it.',
        window_closed:
          'Message left out: WhatsApp allows only template messages 24 hours after the customer last wrote.',
      },
      noActions: 'Nothing',
    },
    try: {
      title: 'Try it',
      description:
        'Describe a visitor or a chat and see what this rule would do. It is the same check the real rule makes.',
      sample: 'Sample',
      unknown: 'Not known',
      yes: 'Yes',
      no: 'No',
      tagsHint: 'Separate tags with commas.',
      countryHint: 'A two-letter code, such as PK.',
      would: 'This rule would run.',
      wouldNot: 'This rule would not run.',
      verdicts: {
        true: 'True',
        false: 'Not true',
        null: 'Not known, so not true',
      },
      noConditions: 'No conditions: it runs every time the event happens.',
      preview: 'What the customer sees',
      previewIn: 'In {{language}}, the visitor’s language.',
      noWords: 'This rule says nothing to the customer.',
    },
  },
  devices: { desktop: 'Computer', mobile: 'Phone', tablet: 'Tablet' },
  owners: { ai: 'The AI', waiting: 'Nobody yet (waiting for a person)', person: 'A person' },
  priorities: { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' },
  summary: {
    sentence: '{{event}}, if {{conditions}} → {{actions}}',
    always: '{{event}} → {{actions}}',
    aTeam: 'a team',
    owners: { ai: 'the AI', waiting: 'nobody yet', person: 'a person' },
    devices: { desktop: 'a computer', mobile: 'a phone', tablet: 'a tablet' },
    priorities: { urgent: 'urgent', high: 'high', medium: 'medium', low: 'low' },
    events: {
      page_open: 'When a visitor opens a page',
      page_view: 'When a visitor has been on a page for {{duration}}',
      chat_started: 'When a chat starts',
      customer_waiting: 'When a customer has waited {{duration}} for a reply',
      chat_closed: 'When a chat closes',
      offline_message: 'When a message is left while nobody can answer',
    },
    fields: {
      page_url: 'the page address',
      referrer: 'the referrer',
      visit_count: 'the number of visits',
      country: 'the country',
      device: 'the device',
      language: 'the language',
      signed_in: 'signed in',
      hours_open: 'business hours',
      anyone_online: 'someone online',
      owner: 'who has the chat',
      tag: 'the tag',
      team: 'the team',
    },
    ops: {
      contains: '{{field}} contains {{value}}',
      starts_with: '{{field}} starts with {{value}}',
      is: '{{field}} is {{value}}',
      not_contains: '{{field}} does not contain {{value}}',
      at_least: '{{field}} is at least {{value}}',
      at_most: '{{field}} is at most {{value}}',
      is_not: '{{field}} is not {{value}}',
    },
    flags: {
      hours_open: { yes: 'it is within business hours', no: 'it is outside business hours' },
      anyone_online: { yes: 'someone on the team is online', no: 'nobody on the team is online' },
      signed_in: { yes: 'the customer is signed in', no: 'the customer is not signed in' },
    },
    actions: {
      show_prompt: 'show a prompt',
      send_message: 'send a message',
      add_note: 'add a private note',
      add_tag: 'add the tag {{tag}}',
      set_priority: 'set the priority to {{priority}}',
      assign_team: 'send it to {{team}}',
      make_ticket: 'make a ticket',
    },
  },
};
